import React from "react";
import { motion } from "framer-motion";
import {
  Calculator,
  Bot,
  MessageSquare,
  Server,
  Shield,
} from "lucide-react";

const categories = [
  {
    label: "Front Office AI Products",
    cost: "$3,546",
    icon: Bot,
    accentColor: "text-blue-600",
    accentBg: "bg-blue-50",
    accentBorder: "border-blue-100",
  },
  {
    label: "Communication & Marketing Suite",
    cost: "$750",
    icon: MessageSquare,
    accentColor: "text-purple-600",
    accentBg: "bg-purple-50",
    accentBorder: "border-purple-100",
  },
  {
    label: "PMS, Imaging & Infrastructure",
    cost: "$1,400",
    icon: Server,
    accentColor: "text-red-600",
    accentBg: "bg-red-50",
    accentBorder: "border-red-100",
  },
  {
    label: "Back Office RCM & Billing",
    cost: "$1,775 + 3.5%",
    icon: Shield,
    accentColor: "text-amber-600",
    accentBg: "bg-amber-50",
    accentBorder: "border-amber-100",
  },
];

const TotalPricingComparisonSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Background blurs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-100/30 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-100/30 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />

      {/* Dot Pattern Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="max-w-[1000px] w-full flex flex-col items-center relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-red-600 text-sm font-semibold mb-3 border border-red-100">
            <Calculator className="w-4 h-4" />
            <span>Full Market Cost</span>
          </div>

          <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 mb-3 tracking-tight leading-tight">
            The <span className="text-red-500">true cost</span> of your current
            tech stack
          </h2>
          <p className="text-sm text-slate-500 max-w-2xl mx-auto">
            When you add up every vendor, module, and hidden fee across 6–10
            separate tools — the number is staggering.
          </p>
        </motion.div>

        {/* Category Rows */}
        <div className="w-full space-y-3 mb-6">
          {categories.map((cat, i) => (
            <motion.div
              key={cat.label}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.1, duration: 0.4 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 px-6 py-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-10 h-10 rounded-xl ${cat.accentBg} ${cat.accentBorder} border flex items-center justify-center flex-shrink-0`}
                >
                  <cat.icon className={`w-5 h-5 ${cat.accentColor}`} />
                </div>
                <span className="text-base font-semibold text-slate-800">
                  {cat.label}
                </span>
              </div>
              <span className={`text-xl font-bold ${cat.accentColor}`}>
                {cat.cost}
                <span className="text-sm font-medium text-slate-400">
                  /mo
                </span>
              </span>
            </motion.div>
          ))}
        </div>

        {/* Total */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="w-full"
        >
          <div className="bg-white rounded-2xl border-2 border-red-200 shadow-lg px-6 py-5 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-red-400 uppercase tracking-wider block mb-0.5">
                Total Monthly Cost (Competitors)
              </span>
              <span className="text-xs text-slate-400">
                Across 6–10 separate vendors, before any hidden fees
              </span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-4xl font-extrabold text-red-500 tracking-tight tabular-nums">
                $7,471
              </span>
              <span className="text-xl font-bold text-red-400">+ 3.5%</span>
              <span className="text-sm text-red-300 font-medium">/mo</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TotalPricingComparisonSlide;
